import Button from '@alltrails/shared/denali/components/Button';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import Close from '@alltrails/shared/icons/Close';
import Typography from '@alltrails/shared/denali/components/Typography';
import PortalModal from 'components/shared/PortalModal/PortalModal';
import useIsMobile from '@alltrails/shared/hooks/useIsMobile';
import Logo from '@alltrails/shared/denali/components/Logo';
import StarRating from '@alltrails/shared/denali/components/StarRating';
import { FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import InstallPromptType from '@alltrails/analytics/enums/InstallPromptType';
import InstallPromptLocation from '@alltrails/analytics/enums/InstallPromptLocation';
import InstallPromptSource from '@alltrails/analytics/enums/InstallPromptSource';
import logInstallPromptViewed from '@alltrails/analytics/events/logInstallPromptViewed';
import logInstallPromptDismissed from '@alltrails/analytics/events/logInstallPromptDismissed';
import { useEffect } from 'react';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';
import * as styles from './styles/styles.module.scss';
import { deepLinkRedirect, isAndroid } from '../utils';
import editorsChoiceSrc from './editors-choice.svg';
import iosFlyoverVideoSrc from './iosFlyoverVideo.mp4';
import androidFlyoverVideoSrc from './androidFlyoverVideo.mp4';
import useDeeplink from '../hooks/useDeeplink';

type InterstitialProps = {
  promptLocation: InstallPromptLocation;
  pageId?: number;
  isOpen?: boolean;
  onRequestClose?: () => void;
  displayTrigger?: CarouselDisplayTrigger;
};

// Design link: https://www.figma.com/file/eQkMBkn2YnhYkVahhaXrZG/In-House-Branch?type=design&node-id=728-29737&mode=design&t=7RuTtVuDopgfLlLh-0

const Interstitial = ({ promptLocation, pageId, isOpen = false, onRequestClose, displayTrigger }: InterstitialProps) => {
  const isMobile = useIsMobile();
  const deeplink = useDeeplink('trail', pageId);

  const intl = useIntl();
  const ctaText = intl.formatMessage({ defaultMessage: 'Get the app' });

  useEffect(() => {
    if (isOpen && isMobile) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: InstallPromptType.Interstitial,
        install_prompt_source: InstallPromptSource.Native,
        signup_modal_trigger: displayTrigger
      });
    }
  }, [isOpen, promptLocation, displayTrigger, isMobile]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: InstallPromptType.Interstitial,
      install_prompt_source: InstallPromptSource.Native
    });
    onRequestClose();
  };

  return (
    <PortalModal isOpen={isOpen && isMobile}>
      <div className={styles.container}>
        <IconButton
          className={styles.close}
          icon={{ Component: Close }}
          onClick={handleCloseClick}
          size="md"
          title={intl.formatMessage({ defaultMessage: 'Close' })}
          testId="close-button"
          variant="flat"
        />
        <div className={styles.contentContainer}>
          <Logo color="neon" size="sm" variant="logomark" />
          <div className={styles.textContainer}>
            <Typography variant="heading500" className={styles.text}>
              <FormattedMessage defaultMessage="Try 3D trail previews and more in the app" />
            </Typography>
            <div className={styles.ratingContainer}>
              <div className={styles.starRating}>
                <StarRating rating={4.9} size="sm" ratingSide="right" variant="simple" />
                <span>4.9</span>
              </div>
              <img alt="" src={editorsChoiceSrc} />
            </div>
          </div>
          <div className={styles.phoneContainer}>
            <video autoPlay loop muted playsInline>
              <source src={isAndroid() ? androidFlyoverVideoSrc : iosFlyoverVideoSrc} type="video/mp4" />
            </video>
          </div>
          <div className={styles.buttonContainer}>
            <Button
              text={ctaText}
              onClick={() => deepLinkRedirect(deeplink, InstallPromptType.Interstitial, promptLocation)}
              className={styles.button}
              testId="Interstitial-button"
              variant="elevated"
            />
          </div>
        </div>
      </div>
    </PortalModal>
  );
};

export default Interstitial;
